import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  padding: 6rem 1.5rem 5rem;
  background: rgb(var(--bg));
  min-height: 100vh;
  max-width: 1180px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 5rem 1.25rem 4rem;
  }

  @media (max-width: 480px) {
    padding: 4.5rem 1rem 3rem;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
  padding: 2.25rem 1.5rem 1.9rem;
  border-radius: 22px;
  background: rgb(var(--fg) / 0.035);
  color: rgb(var(--fg));
  border: 1px solid rgb(var(--fg) / 0.06);
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 20%, rgba(77, 159, 255, 0.15), transparent 45%),
      radial-gradient(circle at 82% 22%, rgba(77, 159, 255, 0.08), transparent 35%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 600px) {
    padding: 1.9rem 1rem 1.5rem;
    margin-bottom: 32px;
    border-radius: 18px;
  }
`;

const Title = styled.h1`
  font-size: 2.7rem;
  color: rgb(var(--fg) / 0.95);
  margin: 0;
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: -0.03em;

  @media (max-width: 600px) {
    font-size: 2.1rem;
  }
`;

const Subtitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 500;
  color: rgb(var(--fg) / 0.78);
  margin: 10px 0 12px;
  font-style: italic;
  line-height: 1.6;

  @media (max-width: 600px) {
    font-size: 1rem;
  }
`;

const DatePublished = styled.p`
  font-size: 0.9rem;
  color: rgb(var(--gray) / 0.7);
  margin: 0 0 20px;
  letter-spacing: 0.02em;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-bottom: 0;
  margin-top: 4px;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  color: rgb(var(--fg) / 0.9);
  font-size: 0.82rem;
  padding: 7px 14px;
  border-radius: 999px;
  font-weight: 600;
  user-select: none;
  border: 1px solid rgba(77, 159, 255, 0.3);
  backdrop-filter: blur(4px);
  transition: transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.08);
    box-shadow: 0 10px 20px rgb(0 0 0 / calc(0.3 * var(--shadow-mul)));
  }
`;

const CoverImage = styled.img`
  display: block;
  width: 100%;
  max-width: 860px;
  height: 360px;
  object-fit: cover;
  object-position: center;
  margin: 1.5rem auto 1rem;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 18px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  background: rgb(var(--fg) / 0.04);
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s ease;

  &:hover {
    filter: brightness(1) saturate(1);
  }

  @media (max-width: 768px) {
    height: 260px;
  }

  @media (max-width: 480px) {
    height: 200px;
    max-width: 100%;
  }
`;

const Section = styled.section`
  margin-bottom: 1.75rem;
  padding: 1.6rem 1.45rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));

  @media (max-width: 600px) {
    margin-bottom: 1.25rem;
    padding: 1.2rem 1rem;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.45rem;
  color: #4d9fff;
  margin-bottom: 16px;
  border-bottom: 1px solid rgba(77, 159, 255, 0.25);
  padding-bottom: 10px;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 1.2rem;
    margin-bottom: 12px;
    padding-bottom: 8px;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: rgb(var(--fg) / 0.82);
  margin: 0 0 1rem 0;

  @media (max-width: 600px) {
    font-size: 0.95rem;
    line-height: 1.75;
  }
`;

const Image = styled.img`
  display: block;
  max-width: 680px;
  width: 100%;
  margin: 20px auto 10px;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 16px 34px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  background: rgb(var(--fg) / 0.04);
  filter: brightness(0.92) saturate(0.95);
`;

const Caption = styled.figcaption`
  font-size: 0.9rem;
  color: rgb(var(--gray) / 0.7);
  text-align: center;
  margin: 12px auto 20px;
  max-width: 680px;
  background: rgb(var(--fg) / 0.04);
  border-radius: 12px;
  padding: 0.6rem 0.75rem;
  border: 1px solid rgb(var(--fg) / 0.06);
`;

const Figure = styled.figure`
  margin: 0;
`;

const CodeBlock = styled.pre`
  background: rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  color: rgb(var(--fg) / 0.86);
  padding: 1.15rem;
  border-radius: 16px;
  font-family: 'Source Code Pro', monospace, monospace;
  font-size: 0.88rem;
  line-height: 1.65;
  overflow-x: auto;
  margin: 20px 0;
  border: 1px solid rgb(var(--fg) / 0.06);
  box-shadow: 0 16px 36px rgb(0 0 0 / calc(0.35 * var(--shadow-mul)));

  @media (max-width: 600px) {
    font-size: 0.8rem;
    padding: 0.95rem;
    border-radius: 14px;
  }
`;

const BackButtonWrapper = styled.div`
  text-align: center;
  margin-top: 28px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--fg) / 0.06);
  color: rgb(var(--fg) / 0.92);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  padding: 0.9rem 2rem;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  user-select: none;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(77, 159, 255, 0.5);
  }

  @media (max-width: 600px) {
    width: 100%;
    padding: 0.85rem 1.5rem;
    font-size: 0.78rem;
  }
`;

export const EventPipelineMetadata = {
  title: "G Suite Automation Pipeline",
  description: "End-to-end automation of event registration, check-ins, certificates, and feedback using Google Workspace and Moosend, replacing manual workflows with a fully standardized system.",
  tags: ['Automation', 'Google Workspace', 'Apps Script', 'APIs', 'Process Design'],
  languages: ['JavaScript', 'Apps Script'],
  datePublished: '2025-04-15',
  coverImage: '/images/g_suite.png',
  path: '/projects/event-pipeline',
};

const EventPipeline = () => {
  const navigate = useNavigate();
  return (
    <Container>
      <ContentWrapper>
        <Header>
          <Title>G Suite Automation Pipeline</Title>
          <Subtitle>Event Registration, Check‑In & Feedback Automation System</Subtitle>
          <DatePublished>Published on April 15, 2025</DatePublished>
          <CoverImage src={EventPipelineMetadata.coverImage} alt="G Suite Automation Pipeline Cover" />
          <TagsContainer>
            {EventPipelineMetadata.tags.map(tag => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </TagsContainer>
        </Header>

        <Section>
          <SectionTitle>Context &amp; Problem</SectionTitle>
          <Paragraph>
            Managing event registrations, attendee check-ins, feedback collection, and certificate issuance through manual processes proved inefficient and prone to error. The workflow relied on multiple disconnected tools and frequent data transfers between forms and spreadsheets, creating delays, inconsistencies, and a high likelihood of mistakes during busy periods.
          </Paragraph>
          <Paragraph>
            Beyond the operational friction, the process required coordination across several departments, each responsible for different steps in the event lifecycle. This dependency chain increased the risk of miscommunication and made timelines harder to control. As a result, staff often spent significant time verifying entries, resolving discrepancies, and ensuring that attendees received accurate information, ultimately limiting the organization’s ability to scale its events smoothly.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Solution Overview</SectionTitle>
          <Paragraph>
            This project automates the entire event workflow by integrating Google Forms, Sheets, Slides, and Gmail through Google Apps Script. The automation centralizes registration data, generates personalized certificates, manages email communication, and coordinates feedback collection. It also connects with the Moosend API to handle targeted campaigns and coupon distribution, extending the system’s functionality beyond internal processes to participant-facing engagement.
          </Paragraph>
          <Paragraph>
            Although each event requires a one-time setup—linking forms, uploading assets, configuring templates, and adjusting event‑specific messaging—the operational workload drops to virtually zero afterward. Once the pipeline is initiated, it handles every step independently: registration logging, QR‑based check‑ins, certificate generation, communication triggers, and the preparation of post‑event materials. This replaces a workflow that previously spanned multiple departments and relied heavily on manual coordination.
          </Paragraph>
          <Paragraph>
            The system’s structure emphasizes modularity and reusability. Core logic functions are designed to adapt to event-specific resources without modification, and its integration with Moosend supports consistent follow‑up communication. This combination allows the pipeline to scale easily across different event formats while maintaining a stable and predictable flow of information.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Pipeline Flow</SectionTitle>

          <Paragraph>
            This section outlines the automated event workflow and details how information moves through the system from registration to post‑event communication. Each component plays a defined role, ensuring that every participant action triggers a predictable downstream process.
          </Paragraph>
          <Paragraph>
            Every event begins with a standardized folder architecture. After configuring the event‑specific forms, spreadsheets, and templates once, the automation operates independently—capturing inputs, updating repositories, generating documents, and initiating communication without additional intervention.
          </Paragraph>
          <Paragraph>
            The project is anchored around a structured Google Drive directory that organizes all assets and datasets required for the workflow:
          </Paragraph>

          <CodeBlock>{`Event_Folder/
├── Before/
│   ├── Registration Form (Google Forms)
│   ├── Registration Responses (Google Sheets)
│   ├── Check-In Form (Google Forms)
│   ├── Check-In Responses (Google Sheets)
│   └── Moosend (Google Sheets)
├── After/
│   ├── Certificates Folder/
│   │   └── *All generated certificate PDFs stored here*
│   ├── Certificate Template (Google Slides)
│   ├── Feedback Form (Google Forms)
│   ├── Feedback Responses (Google Sheets)
│   └── generated-coupons.txt (exported list of coupon codes)
└── Assets/
    ├── Forms Banner
    ├── Slider
    └── Poster`}</CodeBlock>

          <Paragraph>
            The pipeline starts when an attendee submits the registration form. Their information is logged instantly into a dedicated Google Sheet, which functions as the central source of truth for all subsequent automation steps.
          </Paragraph>
          <Figure>
            <Image src="/images/gsuite_pipeline_registration_email.png" alt="Registration Confirmation Email" />
            <Caption>Registration Confirmation Email</Caption>
          </Figure>

          <Paragraph>
            The system can also schedule reminder emails when necessary to support attendance rates. Each participant receives a unique QR code functioning as a digital ticket, linked to a prefilled check‑in form that contains their registration details. While the option for self‑check‑in exists, the primary purpose is operational efficiency: staff simply scan the QR code and submit an already completed form, eliminating manual name searches or data entry. The resulting check‑in sheet becomes an accurate, validated participant list that drives downstream actions such as certificate delivery and targeted follow‑up communication.
          </Paragraph>

          <Paragraph>
            Feedback collection is handled through automated email prompts that invite participants to share their impressions, enabling a consistent and structured post‑event evaluation process.
          </Paragraph>
          <Figure>
            <Image src="/images/gsuite_pipeline_feedback_email.png" alt="Feedback Email" />
            <Caption>Feedback Email</Caption>
          </Figure>

          <Paragraph>
            Certificates are generated dynamically through the Google Slides API. The automation personalizes each certificate using attendee data and distributes the final PDF to recipients through Gmail. This approach ensures accuracy, consistency, and a professional presentation across all events.
          </Paragraph>
          <Figure>
            <Image src="/images/gsuite_pipeline_certificate_page.png" alt="Certificate Email and Page" />
            <Caption>Certificate Email and Page</Caption>
          </Figure>

          <Paragraph>
            Once participants complete the feedback form, the system automatically triggers the coupon delivery workflow. Coupon codes—prepared in advance—are retrieved and emailed to attendees, maintaining a smooth and fully automated close to the event lifecycle.
          </Paragraph>
          <Figure>
            <Image src="/images/gsuite_pipeline_coupon_email.png" alt="Coupon Email" />
            <Caption>Coupon Email</Caption>
          </Figure>
        </Section>

        <Section>
          <SectionTitle>Key Scripts &amp; Logic</SectionTitle>

          <Paragraph>
            The automation relies heavily on Google Apps Script to connect Google Forms responses with Sheets for data storage, Slides API to create personalized certificates, Gmail API to send customized emails, and Moosend API to trigger email marketing campaigns.
          </Paragraph>

          <Paragraph><strong>1. Apps Script - Form Submission Trigger</strong></Paragraph>
          <CodeBlock>{`function onFormSubmit(e) {
  const responses = e.values;
  const email = responses[1]; // Attendee email
  // Generate certificate using Slides API
  generateCertificate(email);
  // Send confirmation email
  sendConfirmationEmail(email);
  // Trigger Moosend API for campaign
  sendMoosendCampaign(email);
}`}</CodeBlock>

          <Paragraph><strong>2. Gmail & Sheets Integration</strong></Paragraph>
          <CodeBlock>{`function sendConfirmationEmail(email) {
  GmailApp.sendEmail(email, 'Registration Confirmed', 'Thank you for registering!');
}

function logCheckIn(email) {
  const sheet = SpreadsheetApp.getActiveSpreadsheet().getSheetByName('CheckIns');
  sheet.appendRow([email, new Date()]);
}`}</CodeBlock>

          <Paragraph><strong>3. Moosend API Call (simplified)</strong></Paragraph>
          <CodeBlock>{`function sendMoosendCampaign(email) {
  const apiKey = 'REDACTED';
  const url = 'https://api.moosend.com/v3/subscribers/REDACTED/subscribe.json';
  const payload = {
    Email: email,
    ApiKey: apiKey,
  };
  const options = {
    method: 'post',
    contentType: 'application/json',
    payload: JSON.stringify(payload),
  };
  UrlFetchApp.fetch(url, options);
}`}</CodeBlock>

          <Paragraph>
            Additional logic includes error handling and comprehensive logging to track process success and failures. Dynamic template linking allows certificates and emails to be customized based on event-specific parameters, ensuring flexibility and robustness.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Impact &amp; Outcome</SectionTitle>
          <Paragraph>
            This automation transformed a fully manual, error‑prone workflow into a standardized system capable of supporting larger events with zero additional operational cost.
          </Paragraph>
          <Paragraph><strong>Before:</strong> Event execution required close to 10 hours of manual work across registration processing, email drafting, custom certificate page setups, newsletter opt-ins, feedback preparation, and constant back‑and‑forth between stakeholders. Human error, delays, and inconsistent processes made large events difficult to manage and even harder to scale.</Paragraph>

          <Paragraph><strong>After:</strong> With the automation pipeline, the entire workflow is standardized and autonomous. Once the organizer completes a 2–3 hour setup (including configuration and testing), the system manages registration, check‑ins, certificate generation, email communication, and feedback collection without requiring further involvement from any department.</Paragraph>

          <Paragraph><strong>Operational Efficiency:</strong> The automation replaces dozens of repetitive tasks previously handled by up to three separate individuals. Processes that once stretched across multiple days are now completed instantly and without coordination overhead. Large events (200–300 registrations) run smoothly with minimal preparation time.</Paragraph>

          <Paragraph><strong>Quality &amp; Reliability:</strong> Manual data transfer errors have been eliminated. Every attendee receives accurate and timely communication, and certificates are generated consistently through the templated Slides integration. The unified data repository ensures that downstream processes always operate on clean, validated information.</Paragraph>

          <Paragraph><strong>Scalability &amp; Quotas:</strong> The system has been successfully used for events ranging from 50 to 300 registrations. For events exceeding 100 emails per day (Google’s free-tier limit), the pipeline automatically staggers batches using internal tracking columns and dynamic indexes. A 95‑email safety buffer ensures uninterrupted operation alongside other company‑wide automations.</Paragraph>

          <Paragraph><strong>Cross‑Department Impact:</strong> By consolidating registration, design, communication, and operations into a single automated lifecycle, the pipeline significantly reduces inter‑department dependencies. Teams can redirect effort toward improving event content rather than managing logistics.</Paragraph>
        </Section>

        <Section>
          <SectionTitle>What I Learned</SectionTitle>
          <Paragraph>
            This project significantly strengthened my analytical mindset and systems‑thinking capabilities. Designing an end‑to‑end workflow forced me to approach operational processes the way a business analyst would: by understanding data dependencies, identifying inefficiencies, and structuring a solution that could scale reliably across different event formats.
          </Paragraph>

          <Paragraph>
            Translating a fragmented manual process into a coherent automated system required mapping information flows with precision—what enters the system, where it moves, how it transforms, and which components rely on upstream outputs. This experience deepened my ability to think in terms of architecture rather than isolated tasks, a skill directly aligned with data‑driven process design.
          </Paragraph>

          <Paragraph>
            Handling real‑world constraints such as quota limits, asynchronous triggers, data integrity issues, and cross‑department requirements taught me how to build resilient workflows. Developing safeguards, recovery logic, and reusable components helped me understand how robust analytical systems are engineered in practice.
          </Paragraph>

          <Paragraph>
            Most importantly, this project confirmed how much I enjoy solving operational problems through structured analysis and automation.
          </Paragraph>
        </Section>

        <BackButtonWrapper>
          <BackButton onClick={() => navigate('/projects')}>Back to Projects</BackButton>
        </BackButtonWrapper>
      </ContentWrapper>
    </Container>
  );
};

export default EventPipeline;
